import { of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Configurator, FlightSearch, SkyscannerResultsV3 } from 'types';

export const removeDuplicates = (results: FlightSearch.LiveResults): Observable<FlightSearch.LiveResults> => {
  return of(removeNonAirlineOffers(results.itineraries)).pipe(
    map(it => removeNonEffectiveOffers(it)),
    map(it => removeDuplicateByPrice(it)),
    map(it => removeNonEffectiveStops(it)),
    map(it => holdTheCheapest(it, results.itineraries)),
    mergeMap(it => of({
      sessionId: results.sessionId,
      action: results.action,
      state: results.state,
      itineraries: it,
    }))
  );
}

const hideMashUpItineraries = (itineraries: FlightSearch.Itineraries[]): FlightSearch.Itineraries[] => {
  const showDuplicates = showDuplicatesFunc();

  const isRoundTrip = itineraries.some(it => it.inboundLeg)
  if (!isRoundTrip) {
    return itineraries;
  }

  const trimmed: FlightSearch.Itineraries[] = [];
  
  itineraries.forEach(it => {
    const clone = { ...it };
    const prices = it.prices.filter(x => x.items.length === 1);
    if (prices.length > 0) {
      clone.prices = prices;
      trimmed.push(clone);
    } else if (showDuplicates) {
      clone.duplicate = 'Mush up';
      trimmed.push(clone);
    }
  });

  return trimmed;
}

const holdTheCheapest = (
    trimmed: FlightSearch.Itineraries[],
    origin: FlightSearch.Itineraries[],
  ): FlightSearch.Itineraries[] => {

  const cheapestOffer = origin.min(a => {
    const min = a.prices.min(x => x.items.sum(it => it.price));
    return min.items.sum(it => it.price);
  })

  if (!cheapestOffer) {
    return trimmed;
  }

  if (trimmed.length === 0) {
    return [cheapestOffer];
  }

  const hasCheapestOffer = trimmed.some(it => it.outboundLeg.id === cheapestOffer.outboundLeg.id);

  if (hasCheapestOffer) {
    return trimmed;
  }

  return [...trimmed, cheapestOffer];
}

const showDuplicatesFunc = (): boolean => {
  const testTool: Configurator.State = Configurator.builder();
  return testTool != null && testTool.aviaModule.showDuplicates;
}

export const agentTypeCondition = (): SkyscannerResultsV3.AgentType => {
  const testTool: Configurator.State = Configurator.builder();
  if (!testTool) {
    return SkyscannerResultsV3.AgentType.Airline;
  }

  return testTool.aviaModule.agentTypeCondition();
}



const splitter = (a: FlightSearch.Itineraries[]): [FlightSearch.Itineraries[], FlightSearch.Itineraries[]] => {
  return a.reduce((r, it) => {
    r[it.duplicate ? 0 : 1].push(it);
    return r;
  }, [[] as FlightSearch.Itineraries[], [] as FlightSearch.Itineraries[]]);
}

const removeNonAirlineOffers = (x: FlightSearch.Itineraries[]): FlightSearch.Itineraries[] => {
  const b: FlightSearch.Itineraries[] = [];
  const showDuplicates = showDuplicatesFunc();
  const agentType = agentTypeCondition();

  x.forEach(it => {
    
    const clone = { ...it };
    const airlinesPrices = it.prices
      .filter(it => it.items.some(x => x.price != 0))
      .filter(it => agentType ? it.items.some(x => x.agent.type === agentType) : true);

    if (airlinesPrices.length > 0) {
      clone.prices = airlinesPrices;
      b.push(clone);
    } else if (showDuplicates) {
      clone.duplicate = 'Non airline offer';
      b.push(clone);
    }
  });

  return b;
}

const removeDuplicateByPrice = (x: FlightSearch.Itineraries[]): FlightSearch.Itineraries[] => {

  const showDuplicates = showDuplicatesFunc();
  const [res, filtered] = splitter(x);

  const keyF = (leg?: FlightSearch.Leg): string => {
    if (!leg) {
      return '';
    }
    const key = leg.dates.departureDate.getTime()
      + leg.stations.origin.code
      + leg.dates.arrivalDate.getTime()
      + leg.stations.destination.code
      + leg.stations.stops.length;
    return key;
  }

  const group = filtered.groupBy(it => keyF(it.outboundLeg) + keyF(it.inboundLeg));

  const cheapestPriceOption = (it: FlightSearch.Itineraries): FlightSearch.Price => {
    return it.prices.min(x => x.totalPrice);
  }

  group.forEach((it, k) => {
    const sorted = it.sort((a, b) => cheapestPriceOption(a).totalPrice - cheapestPriceOption(b).totalPrice);
    sorted.forEach((it, i) => {
      if (i === 0) {
        res.push(it);
      } else if (showDuplicates) {
        const clone = { ...it };
        clone.duplicate = 'Duplicate with higher price';
        res.push(clone);
      }
    });
  });

  return res;
}

const duration = (i: FlightSearch.Itineraries): number => {
  return i.outboundLeg.duration.duration + ((i?.inboundLeg) ? i.inboundLeg.duration.duration : 0);
}

const removeNonEffectiveOffers = (a: FlightSearch.Itineraries[]): FlightSearch.Itineraries[] => {

  const showDuplicates = showDuplicatesFunc();
  const [res, filtered] = splitter(a);

  const p = 0.3;
  const durationDelta = 15;

  const minDuration = a.min(it => duration(it));
  const minPrice = filtered.slice().filter(it => duration(it) <= duration(minDuration) + durationDelta).min(it => it.prices[0].totalPrice);
  try {
    const durationBound = duration(minPrice) + (duration(minPrice) * p);

    filtered.forEach(it => {

      if (duration(it) < durationBound || (duration(it) >= durationBound && it.prices[0].totalPrice <= minPrice.prices[0].totalPrice)) {
        res.push(it);
      } else if (showDuplicates) {
        const clone = { ...it };
        clone.duplicate = 'Non effective';
        res.push(clone);
      }
    });
  } catch (e) {

  }


  return res;
}

const removeNonEffectiveStops = (a: FlightSearch.Itineraries[]): FlightSearch.Itineraries[] => {
  const showDuplicates = showDuplicatesFunc();
  const [res, filtered] = splitter(a);

  const stops = (c: FlightSearch.Itineraries): number => {
    return c.outboundLeg.stations.stops.length + (c.inboundLeg ? c.inboundLeg.stations.stops.length : 0);
  }

  filtered.forEach(it => {
    const stopsL = stops(it);
    if (stopsL >= 1) {
      const min = filtered.slice().filter(x => stops(x) < stopsL).min(x => x.prices[0].totalPrice);
      if ((min && it.prices[0].totalPrice < min.prices[0].totalPrice) || !min) {
        res.push(it);
      } else if (showDuplicates) {
        const clone = { ...it };
        clone.duplicate = 'A lot of transfers for that price';
        res.push(clone);
      }
    } else {
      res.push(it);
    }
  });

  return res;
}